import React, { useState, useMemo } from 'react';
import { Line } from 'recharts';
import { calcMA, calcEMA, calcBollinger, calcRSI, calcMACD } from '../utils/chartDrawings';
import { Activity, X, BarChart3 } from 'lucide-react';

/**
 * Available indicator definitions with default params.
 */
export const INDICATORS = {
  sma20: { id: 'sma20', label: 'SMA 20', type: 'overlay', color: '#3b82f6', calc: (v) => calcMA(v, 20) },
  sma50: { id: 'sma50', label: 'SMA 50', type: 'overlay', color: '#f59e0b', calc: (v) => calcMA(v, 50) },
  ema12: { id: 'ema12', label: 'EMA 12', type: 'overlay', color: '#8b5cf6', calc: (v) => calcEMA(v, 12) },
  ema26: { id: 'ema26', label: 'EMA 26', type: 'overlay', color: '#ec4899', calc: (v) => calcEMA(v, 26) },
  bollinger: { id: 'bollinger', label: 'Bollinger (20,2)', type: 'overlay', color: '#06b6d4', calc: (v) => calcBollinger(v, 20, 2) },
  rsi: { id: 'rsi', label: 'RSI 14', type: 'subpanel', color: '#a855f7', calc: (v) => calcRSI(v, 14) },
  macd: { id: 'macd', label: 'MACD (12,26,9)', type: 'subpanel', color: '#22c55e', calc: (v) => calcMACD(v) },
};

/**
 * Indicator selector dropdown.
 */
export function IndicatorSelector({ activeIndicators, onToggle }) {
  const [open, setOpen] = useState(false);

  const overlayIndicators = Object.values(INDICATORS).filter(i => i.type === 'overlay');
  const subpanelIndicators = Object.values(INDICATORS).filter(i => i.type === 'subpanel');

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
          activeIndicators.length > 0
            ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400'
            : 'bg-white/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
        }`}
      >
        <Activity size={12} />
        Indicators
        {activeIndicators.length > 0 && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-bold">
            {activeIndicators.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-1 w-[calc(100vw-2rem)] sm:w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 p-3 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Overlays</h4>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
              <X size={12} />
            </button>
          </div>
          <div className="space-y-1 mb-3">
            {overlayIndicators.map(ind => (
              <label key={ind.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeIndicators.includes(ind.id)}
                  onChange={() => onToggle(ind.id)}
                  className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500"
                />
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ind.color }} />
                <span className="text-xs text-slate-700 dark:text-slate-300">{ind.label}</span>
              </label>
            ))}
          </div>

          <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Sub-panels</h4>
          <div className="space-y-1">
            {subpanelIndicators.map(ind => (
              <label key={ind.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeIndicators.includes(ind.id)}
                  onChange={() => onToggle(ind.id)}
                  className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500"
                />
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ind.color }} />
                <span className="text-xs text-slate-700 dark:text-slate-300">{ind.label}</span>
              </label>
            ))}
          </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Renders overlay indicators (SMA, EMA, Bollinger) as Recharts Line components.
 * Must be placed inside a ComposedChart/AreaChart.
 */
export function IndicatorOverlays({ chartData, activeIndicators, priceKey = 'close' }) {
  const prices = useMemo(() => chartData.map(d => d[priceKey]), [chartData, priceKey]);

  const computed = useMemo(() => {
    const result = {};
    for (const id of activeIndicators) {
      const ind = INDICATORS[id];
      if (!ind || ind.type !== 'overlay') continue;

      if (id === 'bollinger') {
        const bb = ind.calc(prices);
        result.bollingerUpper = bb.upper;
        result.bollingerMiddle = bb.middle;
        result.bollingerLower = bb.lower;
      } else {
        result[id] = ind.calc(prices);
      }
    }
    return result;
  }, [prices, activeIndicators]);

  const lines = [];

  // SMA / EMA lines
  for (const id of activeIndicators) {
    const ind = INDICATORS[id];
    if (!ind || ind.type !== 'overlay' || id === 'bollinger') continue;
    const data = computed[id];
    if (!data) continue;

    // Merge into chartData-compatible format
    lines.push(
      <Line
        key={id}
        dataKey={(entry) => {
          const idx = chartData.indexOf(entry);
          return data[idx]?.value ?? null;
        }}
        stroke={ind.color}
        strokeWidth={1.5}
        dot={false}
        isAnimationActive={false}
        name={ind.label}
      />
    );
  }

  // Bollinger Bands
  if (activeIndicators.includes('bollinger') && computed.bollingerUpper) {
    const bbColor = INDICATORS.bollinger.color;
    lines.push(
      <Line
        key="bb-upper"
        dataKey={(entry) => {
          const idx = chartData.indexOf(entry);
          return computed.bollingerUpper[idx]?.value ?? null;
        }}
        stroke={bbColor}
        strokeWidth={1}
        strokeDasharray="4 2"
        dot={false}
        isAnimationActive={false}
        name="BB Upper"
      />,
      <Line
        key="bb-middle"
        dataKey={(entry) => {
          const idx = chartData.indexOf(entry);
          return computed.bollingerMiddle[idx]?.value ?? null;
        }}
        stroke={bbColor}
        strokeWidth={1}
        dot={false}
        isAnimationActive={false}
        name="BB Middle"
      />,
      <Line
        key="bb-lower"
        dataKey={(entry) => {
          const idx = chartData.indexOf(entry);
          return computed.bollingerLower[idx]?.value ?? null;
        }}
        stroke={bbColor}
        strokeWidth={1}
        strokeDasharray="4 2"
        dot={false}
        isAnimationActive={false}
        name="BB Lower"
      />
    );
  }

  return <>{lines}</>;
}

/**
 * Sub-panel for RSI / MACD indicators (rendered below the main chart).
 */
export function IndicatorSubPanel({ chartData, activeIndicators, priceKey = 'close' }) {
  const prices = useMemo(() => chartData.map(d => d[priceKey]), [chartData, priceKey]);

  const hasRSI = activeIndicators.includes('rsi');
  const hasMACD = activeIndicators.includes('macd');

  if (!hasRSI && !hasMACD) return null;

  return (
    <div className="mt-2 border-t border-slate-100 dark:border-slate-800 pt-2 space-y-2">
      {hasRSI && <RSIPanel prices={prices} chartData={chartData} />}
      {hasMACD && <MACDPanel prices={prices} chartData={chartData} />}
    </div>
  );
}

function RSIPanel({ prices, chartData }) {
  const rsiData = useMemo(() => calcRSI(prices, 14), [prices]);

  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
          <Activity size={10} className="text-purple-500" /> RSI (14)
        </h5>
        {rsiData.length > 0 && rsiData[rsiData.length - 1]?.value != null && (
          <span className={`text-xs font-bold tabular-nums ${
            rsiData[rsiData.length - 1].value > 70
              ? 'text-red-500'
              : rsiData[rsiData.length - 1].value < 30
                ? 'text-green-500'
                : 'text-slate-600 dark:text-slate-400'
          }`}>
            {rsiData[rsiData.length - 1].value.toFixed(1)}
          </span>
        )}
      </div>
      {/* Simple RSI bar visualization */}
      <div className="relative h-8 bg-slate-100 dark:bg-slate-700 rounded overflow-hidden">
        {/* Overbought/Oversold zones */}
        <div className="absolute top-0 left-0 right-0 h-[30%] bg-red-500/10 border-b border-red-500/20" />
        <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-green-500/10 border-t border-green-500/20" />
        {/* Current RSI marker */}
        {rsiData.length > 0 && rsiData[rsiData.length - 1]?.value != null && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-purple-500"
            style={{ left: `${rsiData[rsiData.length - 1].value}%` }}
          />
        )}
        {/* Labels */}
        <span className="absolute top-0.5 right-1 text-[8px] text-red-400">70</span>
        <span className="absolute bottom-0.5 right-1 text-[8px] text-green-400">30</span>
      </div>
    </div>
  );
}

function MACDPanel({ prices, chartData }) {
  const macdData = useMemo(() => calcMACD(prices), [prices]);

  const lastMACD = macdData.macd.filter(m => m.value !== null).pop();
  const lastSignal = macdData.signal.filter(s => s.value !== null).pop();

  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
          <BarChart3 size={10} className="text-green-500" /> MACD (12,26,9)
        </h5>
        <div className="flex items-center gap-3 text-[10px]">
          {lastMACD && (
            <span className={`font-bold tabular-nums ${lastMACD.value >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              MACD: {lastMACD.value.toFixed(4)}
            </span>
          )}
          {lastSignal && (
            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
              Signal: {lastSignal.value.toFixed(4)}
            </span>
          )}
        </div>
      </div>
      {/* Histogram bars */}
      <div className="relative h-10 flex items-end gap-px">
        {macdData.histogram.slice(-50).map((h, i) => {
          if (h.value === null) return <div key={i} className="flex-1" />;
          const maxH = Math.max(...macdData.histogram.filter(x => x.value !== null).map(x => Math.abs(x.value)));
          const height = maxH > 0 ? (Math.abs(h.value) / maxH) * 100 : 0;
          return (
            <div
              key={i}
              className={`flex-1 rounded-t-sm ${h.value >= 0 ? 'bg-green-500/60' : 'bg-red-500/60'}`}
              style={{ height: `${height}%` }}
            />
          );
        })}
      </div>
    </div>
  );
}
